import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Calendar, User, Sparkles } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function PublicUpdates() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch public news updates
    axios
      .get(`${API_URL}/api/public/news`)
      .then((res) => {
        setUpdates(res.data.news || []);
        setLoading(false);
      })
      .catch(() => {
        setUpdates([]);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Hero Section */}
      <div className="border-b border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <Newspaper size={32} className="text-amber-500" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-cinzel text-amber-300 tracking-wide">
              Game Updates
            </h1>
          </div>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
            Stay updated with the latest news, patch notes, and announcements from the arena. Follow Lanista&apos;s development as we add new features, balance gladiator classes, introduce exotic beasts, and expand the game world.
          </p>
          
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-zinc-950 font-cinzel uppercase tracking-wide text-sm transition-colors"
            >
              <Sparkles size={16} />
              Play Free Now
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-amber-900/50 hover:border-amber-600 text-amber-300 font-cinzel uppercase tracking-wide text-sm transition-colors"
            >
              About Lanista
            </Link>
          </div>
        </div>
      </div>

      {/* Updates List */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
            <p className="text-zinc-500 mt-4 text-sm">Loading latest updates...</p>
          </div>
        ) : updates.length === 0 ? (
          <div className="text-center py-12 border border-zinc-800 bg-zinc-950/60 p-8">
            <Newspaper size={48} className="text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No updates available yet. Check back soon for news from the arena!</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">
                Recent Updates
              </h2>
              <Link
                to="/login"
                className="text-xs text-amber-500 hover:text-amber-400 uppercase tracking-wider"
              >
                View All Updates (Login Required) →
              </Link>
            </div>

            {updates.map((update, idx) => (
              <article
                key={update.id}
                className="border border-zinc-800 bg-zinc-950/60 p-5 sm:p-6 hover:border-amber-900/40 transition-colors"
              >
                {/* Update Number Badge */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-900/20 border border-amber-900/40 flex items-center justify-center">
                      <span className="text-xs font-cinzel text-amber-500">#{updates.length - idx}</span>
                    </div>
                    <h3 className="font-cinzel text-lg sm:text-xl text-amber-200 tracking-wide">
                      {update.title}
                    </h3>
                  </div>
                </div>

                {/* Update Body */}
                <div className="ml-0 sm:ml-11">
                  <p className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap">
                    {update.body}
                  </p>

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-4 pt-3 border-t border-zinc-800/50">
                    <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                      <User size={12} />
                      <span>{update.author || "Arena Master"}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                      <Calendar size={12} />
                      <time dateTime={update.created_at}>
                        {fmtDate(update.created_at)}
                      </time>
                    </div>
                    {update.updated_at && (
                      <div className="flex items-center gap-1.5 text-xs text-zinc-600 italic">
                        <span>Updated: {fmtDateTime(update.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-12 border border-amber-900/30 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-6 sm:p-8 text-center">
          <h3 className="font-cinzel text-xl sm:text-2xl text-amber-300 mb-3">
            Ready to Enter the Arena?
          </h3>
          <p className="text-sm sm:text-base text-zinc-400 mb-5 max-w-2xl mx-auto">
            Join thousands of players managing their gladiators, breeding exotic beasts, and battling for glory in tactical PvP combat. Free to play, no download required.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-zinc-950 font-cinzel uppercase tracking-wide text-sm transition-colors"
          >
            <Sparkles size={16} />
            Create Free Account
          </Link>
        </div>

        {/* SEO Footer Links */}
        <div className="mt-10 pt-6 border-t border-zinc-800">
          <nav className="flex flex-wrap gap-4 justify-center text-xs text-zinc-500">
            <Link to="/" className="hover:text-amber-400 transition-colors">
              Home
            </Link>
            <Link to="/rankings" className="hover:text-amber-400 transition-colors">
              Rankings
            </Link>
            <Link to="/tournaments" className="hover:text-amber-400 transition-colors">
              Tournaments
            </Link>
            <Link to="/about" className="hover:text-amber-400 transition-colors">
              About
            </Link>
            <Link to="/contact" className="hover:text-amber-400 transition-colors">
              Contact
            </Link>
          </nav>
        </div>
      </div>
    </div>
  );
}
